import {useEffect, useLayoutEffect, useMemo, useState, useRef} from "react";
import debounce from 'debounce';
import TextInput from "@/Components/TextInput";
import InputLabel from "@/Components/InputLabel";
import Checkbox from "@/Components/Checkbox";
import SecondaryButton from "@/Components/SecondaryButton";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronLeft, faChevronRight, faSearch, faXmark} from "@fortawesome/free-solid-svg-icons";
import {Subtitle} from "@/models/Subtitle";

export type Change = {
    index: number;
    item: Subtitle;
}

type MatchedItem = {
    item: Subtitle;
    originalIndex: number;
}

export type SearchPattern = {
    pattern: string;
    caseSensitive: boolean;
} | null;

type SearchDropdownProps = {
    items: Subtitle[];
    onNavigate: (itemIndex: number) => void;
    onReplaceAll: (changes: Change[]) => void;
    onSearchChange?: (search: SearchPattern) => void;
}

function escapeRegExp(str: string) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export default function SearchDropdown({items, onNavigate, onReplaceAll, onSearchChange}: SearchDropdownProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState<string>('');
    const [replace, setReplace] = useState<string>('');
    const [caseSensitive, setCaseSensitive] = useState<boolean>(false);
    const [useRegex, setUseRegex] = useState<boolean>(false);
    const [matchedItems, setMatchedItems] = useState<MatchedItem[]>([]);
    const [currentMatchIndex, setCurrentMatchIndex] = useState<number>(0);
    const [alignLeft, setAlignLeft] = useState(false);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);

    const getSearchRegex = (searchStr: string) => {
        const str = useRegex ? searchStr : escapeRegExp(searchStr);
        try {
            return new RegExp(`(${str})`, caseSensitive ? 'g' : 'gi');
        } catch {
            return new RegExp(escapeRegExp(searchStr), caseSensitive ? 'g' : 'gi');
        }
    };

    const handleSearch = useMemo(() => debounce((value: string) => {
        if (value === '') {
            setMatchedItems([]);
            setCurrentMatchIndex(0);
            onSearchChange?.(null);
            return;
        }
        const regex = getSearchRegex(value);
        const filtered: MatchedItem[] = [];
        items.forEach((item, index) => {
            if (!item.deleted && item.text.match(regex)) {
                filtered.push({item, originalIndex: index});
            }
        });
        setMatchedItems(filtered);
        setCurrentMatchIndex(0);
        onSearchChange?.({
            pattern: useRegex ? value : escapeRegExp(value),
            caseSensitive
        });
        if (filtered.length > 0) {
            onNavigate(filtered[0].originalIndex);
        }
    }, 300), [items, caseSensitive, useRegex, onSearchChange]);

    useEffect(() => {
        handleSearch(search);
    }, [search, caseSensitive, useRegex]);

    useLayoutEffect(() => {
        if (isOpen && panelRef.current) {
            const rect = panelRef.current.getBoundingClientRect();
            setAlignLeft(rect.left < 0);
        } else {
            setAlignLeft(false);
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) {
            setTimeout(() => searchInputRef.current?.focus(), 100);
        } else {
            onSearchChange?.(null);
        }
    }, [isOpen]);

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                e.stopPropagation();
                closePanel();
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen]);

    const closePanel = () => {
        setIsOpen(false);
        setSearch('');
        setReplace('');
        setMatchedItems([]);
        setCurrentMatchIndex(0);
    };

    const navigateTo = (index: number) => {
        if (matchedItems.length === 0) return;
        const wrappedIndex = ((index % matchedItems.length) + matchedItems.length) % matchedItems.length;
        setCurrentMatchIndex(wrappedIndex);
        onNavigate(matchedItems[wrappedIndex].originalIndex);
    };

    const goToPrev = () => navigateTo(currentMatchIndex - 1);
    const goToNext = () => navigateTo(currentMatchIndex + 1);

    const performReplaceAll = () => {
        if (matchedItems.length === 0) return;
        const regex = getSearchRegex(search);
        const changes: Change[] = matchedItems.map(({item, originalIndex}) => ({
            index: originalIndex,
            item: new Subtitle({...item, text: item.text.replace(regex, replace)})
        }));
        onReplaceAll(changes);
        setSearch('');
        setReplace('');
        setMatchedItems([]);
        setCurrentMatchIndex(0);
        onSearchChange?.(null);
        searchInputRef.current?.focus();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            if (e.shiftKey) {
                goToPrev();
            } else {
                goToNext();
            }
            e.preventDefault();
        }
    };

    return (
        <div className="relative">
            <SecondaryButton
                title="Find and Replace"
                className=" md:flex items-center !px-3 me-2"
                onClick={() => setIsOpen(!isOpen)}
            >
                <FontAwesomeIcon icon={faSearch} className="lg:me-2 md:my-1"/>
                <span className="hidden lg:inline">Find</span>
            </SecondaryButton>

            {isOpen && (
                <div
                    ref={panelRef}
                    className={`absolute ${alignLeft ? 'left-0' : 'right-0'} top-full mt-1 w-[70vw] md:w-80 bg-white dark:bg-gray-800 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50`}
                    onKeyDown={handleKeyDown}
                >
                    <div className="p-4">
                        <div className="flex items-center justify-between mb-1.5 mt-1">
                            <InputLabel className="font-semibold text-sm" value="Find"/>
                            <button
                                type="button"
                                onClick={closePanel}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 -mt-3 -mr-2"
                            >
                                <FontAwesomeIcon icon={faXmark} className="w-4 h-4"/>
                            </button>
                        </div>
                        <div className="relative">
                            <TextInput
                                ref={searchInputRef}
                                className="w-full pr-8 text-sm py-1.5"
                                value={search}
                                placeholder="Search..."
                                onChange={e => setSearch(e.target.value)}
                            />
                        </div>
                        <div className="flex gap-4 mt-2 mb-4">
                            <label className="flex items-center cursor-pointer">
                                <Checkbox
                                    checked={caseSensitive}
                                    onChange={() => setCaseSensitive(!caseSensitive)}
                                />
                                <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Case sensitive</span>
                            </label>
                            <label className="flex items-center cursor-pointer">
                                <Checkbox
                                    checked={useRegex}
                                    onChange={() => setUseRegex(!useRegex)}
                                />
                                <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Regex</span>
                            </label>
                        </div>

                        <InputLabel className="font-semibold text-sm mb-2" value="Replace with"/>
                        <TextInput
                            className="w-full mb-4 text-sm py-1.5"
                            value={replace}
                            onChange={e => setReplace(e.target.value)}
                        />

                        <div className="flex items-center justify-between border-gray-200 dark:border-gray-700">
                            <SecondaryButton
                                disabled={matchedItems.length === 0}
                                onClick={performReplaceAll}
                                className="text-sm !py-1.5"
                            >
                                Replace all
                            </SecondaryButton>

                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600 dark:text-gray-400 min-w-[70px] text-center">
                                    {matchedItems.length > 0
                                        ? `${currentMatchIndex + 1} of ${matchedItems.length}`
                                        : '0 of 0'
                                    }
                                </span>
                                <div className="flex gap-1">
                                    <button
                                        type="button"
                                        disabled={matchedItems.length === 0}
                                        onClick={goToPrev}
                                        className="p-1.5 rounded text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <FontAwesomeIcon icon={faChevronLeft} className="w-3 h-3"/>
                                    </button>
                                    <button
                                        type="button"
                                        disabled={matchedItems.length === 0}
                                        onClick={goToNext}
                                        className="p-1.5 rounded text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <FontAwesomeIcon icon={faChevronRight} className="w-3 h-3"/>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
